"use client"

import { useMemo } from "react"
import { useFormik, FormikProvider } from "formik"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { FormInput, FormSelect, FormTextarea } from "@/components/form-inputs"
import { useAdjustPartnerBalanceMutation } from "@/hooks/mutations/partners"
import { formatMicroUsd, parseUsdToMicroUsd } from "@/lib/billing/utils/format"
import type { AdminPartnerView } from "@/server-actions/admin-partners"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





type Direction = "credit" | "debit"

type AdjustBalanceForm = {
  direction: Direction
  amount: string
  note: string
}

function getDIRECTION_OPTIONS(tUi: ReturnType<typeof useTranslations<"Partners.PartnersAdminAdjustBalanceDialog">>): readonly { value: Direction; label: string }[] {
  return [
  { value: "credit", label: tUi("creditAddToTheBalance") },
  { value: "debit", label: tUi("debitTakeBackFromTheBalance") },
]
}

/** The only way to correct a partner balance: refunds do not claw commission back automatically. */
export function AdjustBalanceDialog({
  open,
  partner,
  onOpenChange,
}: {
  open: boolean
  partner: AdminPartnerView
  onOpenChange: (open: boolean) => void
}) {


  const tUi = useTranslations("Partners.PartnersAdminAdjustBalanceDialog")
  const DIRECTION_OPTIONS = getDIRECTION_OPTIONS(tUi)


  const mutation = useAdjustPartnerBalanceMutation()
  // One reference per dialog open, so a double submit replays instead of adjusting twice.
  const reference = useMemo(() => (open ? crypto.randomUUID() : ""), [open])

  const formik = useFormik<AdjustBalanceForm>({
    initialValues: { direction: "debit", amount: "", note: "" },
    validate: values => validate(values, partner.balance_microusd),
    onSubmit: async values => {
      const microusd = toMicrousd(values.amount)
      if (microusd === null) return

      try {
        await mutation.mutateAsync({
          partner_id: partner.partner_id,
          amount_microusd: values.direction === "credit" ? microusd : -microusd,
          note: values.note.trim(),
          reference,
        })
        formik.resetForm()
        onOpenChange(false)
      } catch {
        // withQueryHandling already surfaced the error.
      }
    },
  })

  return (
    <Dialog open={open} onOpenChange={next => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <DialogHeader>
              <DialogTitle><span key={tUi("adjustPartnerBalance")}>{tUi("adjustPartnerBalance")}</span></DialogTitle>
              <DialogDescription><TranslationText>{tUi.rich("value0Value1AvailableTheNoteIsStoredOn", { value0: String(partner.tenant_name), value1: String(formatMicroUsd(partner.balance_microusd)) })}</TranslationText></DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-5">
              <FormSelect<Direction> name="direction" label={tUi("direction")} options={DIRECTION_OPTIONS} />
              <FormInput name="amount" label={tUi("amountUSD")} inputMode="decimal" autoFocus />
              <FormTextarea name="note" label={tUi("reason")} maxLength={500} rows={3} />
            </div>

            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <span key={tUi("applyAdjustment")}>{tUi("applyAdjustment")}</span></Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function validate(values: AdjustBalanceForm, balanceMicrousd: number) {
  const errors: Partial<Record<keyof AdjustBalanceForm, string>> = {}
  const microusd = toMicrousd(values.amount)

  if (microusd === null || microusd <= 0) {
    errors.amount = "Enter an amount."
  } else if (values.direction === "debit" && microusd > balanceMicrousd) {
    errors.amount = `The balance only holds ${formatMicroUsd(balanceMicrousd)}.`
  }

  if (!values.note.trim()) {
    errors.note = "Say what this adjustment is for."
  }

  return errors
}

function toMicrousd(amount: string): number | null {
  return parseUsdToMicroUsd(amount.replace(",", "."))
}
